import React, { useState, useEffect } from 'react';
import { db, storage } from '../firebaseConfig';
import { collection, query, where, getDocs, addDoc, doc, updateDoc, orderBy, limit, writeBatch, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

// SellingStatus Component
const SellingStatus = ({ percentage }) => {
    let status = '', color = '';
    if (percentage === null || isNaN(percentage)) return null;
    if (percentage >= 80) { status = 'Profit'; color = 'var(--brand-success)'; }
    else if (percentage >= 50) { status = 'Average'; color = 'var(--brand-warning)'; }
    else if (percentage >= 30) { status = 'Poor'; color = '#fd7e14'; }
    else if (percentage >= 1) { status = 'Bad'; color = 'var(--brand-danger)'; }
    else { status = 'Loss'; color = '#343a40'; }
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ height: '20px', width: '20px', backgroundColor: color, borderRadius: '50%' }}></span>
            <span style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>{status}</span>
        </div>
    );
};

const categoryPrefixMap = {
    "Traditional Jewels": "TTJ", "Kundans": "KND", "Kemps": "KMP", "Chokers": "CHK", "Chains": "CHN",
    "Rings": "RNG", "Bangles": "BGL", "Earrings": "ERG", "American Diamond": "AD", "Shoes": "SHS",
    "Bags": "BAG", "Interiors": "INT", "Statues": "STU", "Ornaments": "ORN", "Elixirs": "ELX",
    "Gifts": "GFT", "Sea Treasure": "STR", "Victorian": "VIC", "Test1": "TS1", "Test2": "TS2", "Others": "OTR"
};

const toDate = (timestamp) => {
    if (!timestamp) return null;
    if (timestamp.toDate) return timestamp.toDate();
    if (timestamp instanceof Date) return timestamp;
    return null;
};

const ProductForm = ({ user, setView, productIdToEdit, onAddNew }) => {
    const [category, setCategory] = useState('Traditional Jewels');
    const [itemName, setItemName] = useState('');
    const [itemDescription, setItemDescription] = useState('');
    const [material, setMaterial] = useState('');
    const [linkedProductId, setLinkedProductId] = useState('');
    const [productId, setProductId] = useState('');
    const [purchaseDate, setPurchaseDate] = useState(new Date());
    const [currency, setCurrency] = useState('INR');
    const [pricePerItem, setPricePerItem] = useState('');
    const [totalItems, setTotalItems] = useState(1);
    const [totalItemPrice, setTotalItemPrice] = useState(0);
    const [totalWeight, setTotalWeight] = useState('');
    const [vendorShippingMethod, setVendorShippingMethod] = useState('Agent');
    const [vendorShippingDate, setVendorShippingDate] = useState(null);
    const [vendorShippingCost, setVendorShippingCost] = useState('');
    const [weightPerItem, setWeightPerItem] = useState(0);
    const [costPerKgVendor, setCostPerKgVendor] = useState(0);
    const [totalPurchaseCost, setTotalPurchaseCost] = useState(0);
    const [exchangeRate, setExchangeRate] = useState(null);
    const [currencyApiError, setCurrencyApiError] = useState(null);
    const [vendors, setVendors] = useState([]);
    const [selectedVendorId, setSelectedVendorId] = useState('');
    const [agents, setAgents] = useState([]);
    const [selectedAgentId, setSelectedAgentId] = useState('');
    const [agentShippingCost, setAgentShippingCost] = useState('');
    const [agentShippingDate, setAgentShippingDate] = useState(null);
    const [costPerKgAgent, setCostPerKgAgent] = useState(0);
    const [totalImportCost, setTotalImportCost] = useState(0);
    const [totalLandingCostMYR, setTotalLandingCostMYR] = useState(0);
    const [perUnitCostMYR, setPerUnitCostMYR] = useState(0);
    const [sellingPriceMYR, setSellingPriceMYR] = useState('');
    const [profitMYR, setProfitMYR] = useState(0);
    const [profitPercentage, setProfitPercentage] = useState(null);
    const [receivedDate, setReceivedDate] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState('');
    const [existingImageUrl, setExistingImageUrl] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState('');
    const categories = Object.keys(categoryPrefixMap);
    
    useEffect(() => {
        const generateProductId = async () => {
            if (!user || !category || productIdToEdit) return;
            const prefix = categoryPrefixMap[category];
            if (!prefix) return;
            const q = query(collection(db, 'products'), where('userId', '==', user.uid), where('productId', '>=', prefix), where('productId', '<', prefix + 'z'), orderBy('productId', 'desc'), limit(1));
            const querySnapshot = await getDocs(q);
            let newIdNumber = 1;
            if (!querySnapshot.empty) {
                const lastProductId = querySnapshot.docs[0].data().productId;
                const lastNumber = parseInt(lastProductId.replace(prefix, ''), 10);
                newIdNumber = lastNumber + 1;
            }
            const formattedId = `${prefix}${String(newIdNumber).padStart(3, '0')}`;
            setProductId(formattedId);
        };
        generateProductId();
    }, [category, user, productIdToEdit]);

    useEffect(() => {
        if (imageFile) {
            const objectUrl = URL.createObjectURL(imageFile);
            setImagePreview(objectUrl);
            return () => URL.revokeObjectURL(objectUrl);
        } else {
            setImagePreview(existingImageUrl);
        }
    }, [imageFile, existingImageUrl]);

    const handleImageChange = (e) => {
        if (e.target.files[0]) {
            setImageFile(e.target.files[0]);
        }
    };
    
    useEffect(() => {
        const fetchRelatedData = async () => {
            if (!user) return;
            const vQuery = query(collection(db, "contacts"), where("userId", "==", user.uid), where("contactType", "==", "Vendor"));
            const aQuery = query(collection(db, "contacts"), where("userId", "==", user.uid), where("contactType", "==", "Agent"));
            const [vSnap, aSnap] = await Promise.all([getDocs(vQuery), getDocs(aQuery)]);
            setVendors(vSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setAgents(aSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        };

        const fetchProductData = async () => {
            if (productIdToEdit) {
                const productRef = doc(db, "products", productIdToEdit);
                const docSnap = await getDoc(productRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setCategory(data.category || 'Traditional Jewels');
                    setProductId(data.productId || '');
                    setItemName(data.itemName || '');
                    setItemDescription(data.itemDescription || '');
                    setMaterial(data.material || '');
                    setLinkedProductId(data.linkedProductId || '');
                    setPurchaseDate(toDate(data.purchaseDate));
                    setCurrency(data.currency || 'INR');
                    setPricePerItem(data.pricePerItem || '');
                    setTotalItems(data.totalItems || 1);
                    setTotalWeight(data.totalWeight || '');
                    setVendorShippingMethod(data.vendorShippingMethod || 'Agent');
                    setVendorShippingDate(toDate(data.vendorShippingDate));
                    setVendorShippingCost(data.vendorShippingCost || '');
                    setSelectedVendorId(data.vendorId || '');
                    setSelectedAgentId(data.agentId || '');
                    setAgentShippingCost(data.agentShippingCost || '');
                    setAgentShippingDate(toDate(data.agentShippingDate));
                    setSellingPriceMYR(data.sellingPriceMYR || '');
                    setReceivedDate(toDate(data.receivedDate));
                    setExistingImageUrl(data.imageUrl || '');
                }
            }
        };

        fetchRelatedData();
        fetchProductData();
    }, [user, productIdToEdit]);

    useEffect(() => { setTotalItemPrice((parseFloat(pricePerItem) || 0) * (parseInt(totalItems, 10) || 0)); }, [pricePerItem, totalItems]);
    useEffect(() => { setTotalPurchaseCost(totalItemPrice + (parseFloat(vendorShippingCost) || 0)); }, [totalItemPrice, vendorShippingCost]);
    useEffect(() => { if (parseFloat(totalWeight) > 0 && totalItems > 0) setWeightPerItem(parseFloat(totalWeight) / totalItems); else setWeightPerItem(0);}, [totalWeight, totalItems]);
    useEffect(() => { if (parseFloat(vendorShippingCost) > 0 && parseFloat(totalWeight) > 0) setCostPerKgVendor(parseFloat(vendorShippingCost) / parseFloat(totalWeight)); else setCostPerKgVendor(0);}, [vendorShippingCost, totalWeight]);
    useEffect(() => { if (parseFloat(agentShippingCost) > 0 && parseFloat(totalWeight) > 0) setCostPerKgAgent(parseFloat(agentShippingCost) / parseFloat(totalWeight)); else setCostPerKgAgent(0); }, [agentShippingCost, totalWeight]);
    useEffect(() => { setTotalImportCost(totalPurchaseCost + (parseFloat(agentShippingCost) || 0)); }, [totalPurchaseCost, agentShippingCost]);
    useEffect(() => { if (totalImportCost > 0 && exchangeRate) { setTotalLandingCostMYR(totalImportCost * exchangeRate); } else { setTotalLandingCostMYR(0); } }, [totalImportCost, exchangeRate]);
    useEffect(() => { if (totalLandingCostMYR > 0 && totalItems > 0) { setPerUnitCostMYR(totalLandingCostMYR / totalItems); } else { setPerUnitCostMYR(0); } }, [totalLandingCostMYR, totalItems]);
    useEffect(() => { const sellingPrice = parseFloat(sellingPriceMYR) || 0; if (sellingPrice > 0 && perUnitCostMYR > 0) { const profit = sellingPrice - perUnitCostMYR; setProfitMYR(profit); setProfitPercentage((profit / perUnitCostMYR) * 100); } else { setProfitMYR(0); setProfitPercentage(null); } }, [sellingPriceMYR, perUnitCostMYR]);
    useEffect(() => { if (!process.env.REACT_APP_EXCHANGE_RATE_API_KEY) { setCurrencyApiError('API Key not found.'); } }, []);
    useEffect(() => { if (!currency || currencyApiError === 'API Key not found.') return; setExchangeRate(null); setCurrencyApiError(null); const apiKey = process.env.REACT_APP_EXCHANGE_RATE_API_KEY; fetch(`https://v6.exchangerate-api.com/v6/${apiKey}/latest/${currency}`).then(res => res.json()).then(data => { if(data.result === "success") setExchangeRate(data.conversion_rates.MYR); else setCurrencyApiError(data['error-type']); }).catch(err => setCurrencyApiError('Network error.')); }, [currency, currencyApiError]);
    
    const showAsMYR = (amount) => { if (exchangeRate && amount > 0) return (<span className="myr-conversion">(RM {(amount * exchangeRate).toFixed(2)})</span>); return null; };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const finalTotalItems = parseInt(totalItems, 10);
        if (!user || !productId) { setSubmitMessage("Error: Product ID is missing."); return; }
        if (isNaN(finalTotalItems) || finalTotalItems <= 0) { setSubmitMessage("Total items must be a number greater than zero."); return; }
        setIsSubmitting(true);
        setSubmitMessage("Processing...");

        let uploadedImageUrl = existingImageUrl;
        if (imageFile) {
            setSubmitMessage("Uploading image...");
            const storageRef = ref(storage, `product-images/${Date.now()}-${imageFile.name}`);
            const snapshot = await uploadBytes(storageRef, imageFile);
            uploadedImageUrl = await getDownloadURL(snapshot.ref);
            setSubmitMessage("Image uploaded. Saving data...");
        }

        const productData = {
            userId: user.uid, productId, category, itemName, itemDescription, material, linkedProductId, 
            purchaseDate, currency, pricePerItem: parseFloat(pricePerItem) || 0, totalItems: finalTotalItems,
            totalWeight: parseFloat(totalWeight) || 0, vendorShippingMethod, vendorShippingDate, 
            vendorShippingCost: parseFloat(vendorShippingCost) || 0, agentShippingCost: vendorShippingMethod === 'Agent' ? (parseFloat(agentShippingCost) || 0) : 0, 
            agentShippingDate: vendorShippingMethod === 'Agent' ? agentShippingDate : null, totalLandingCostMYR, perUnitCostMYR, 
            sellingPriceMYR: parseFloat(sellingPriceMYR) || 0, profitMYR, profitPercentage, receivedDate, vendorId: selectedVendorId, 
            agentId: vendorShippingMethod === 'Agent' ? selectedAgentId : '', imageUrl: uploadedImageUrl, lastUpdatedAt: new Date()
        };

        try {
            if (productIdToEdit) {
                const productRef = doc(db, 'products', productIdToEdit);
                await updateDoc(productRef, productData);
                setSubmitMessage("Product batch updated successfully!");
                setTimeout(() => setView(), 2000);
            } else {
                productData.createdAt = new Date();
                const parentDocRef = await addDoc(collection(db, "products"), productData);
                setSubmitMessage("Parent batch saved. Generating individual items...");
                const batch = writeBatch(db);
                const prefix = categoryPrefixMap[category];
                const numericString = productId.replace(prefix, '');
                const numericPartOfParent = parseInt(numericString, 10);
                const childIdPrefix = `${prefix}${numericPartOfParent}`;
                for (let i = 1; i <= finalTotalItems; i++) {
                    const childId = `${childIdPrefix}${String(i).padStart(3, '0')}`;
                    const newItemRef = doc(db, "inventoryItems", childId);
                    batch.set(newItemRef, {
                        parentDocId: parentDocRef.id, parentReadableId: productId, childId,
                        status: 'Available', userId: user.uid, category, itemName,
                        size: '', color: '', itemDescription,
                        purchasePrice: parseFloat(pricePerItem) || 0, currency, purchaseDate,
                        imageUrl: uploadedImageUrl, perUnitCostMYR, sellingPriceMYR, profitMYR, profitPercentage,
                        createdAt: new Date(), lastUpdatedAt: new Date(),
                        editedBy: user.displayName,
                    });
                }
                await batch.commit();
                setSubmitMessage(`Successfully saved batch ${productId} and generated ${finalTotalItems} items.`);
                setTimeout(() => { onAddNew(); }, 2000);
            }
        } catch (error) {
            console.error("Error saving product:", error);
            setSubmitMessage(`Error saving record: ${error.message}`);
        }
        setIsSubmitting(false);
    };

    return (
        <form onSubmit={handleSubmit} className="product-form-container">
            <div className="product-form-main">
                <div className="form-section">
                    <h3>Item Details</h3>
                    <div className="form-grid">
                        <div className="form-field"><label>Category</label><select value={category} onChange={(e) => setCategory(e.target.value)} disabled={!!productIdToEdit}>{categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}</select></div>
                        <div className="form-field"><label>Product ID</label><input type="text" value={productId} readOnly /></div>
                        <div className="form-field"><label>Item Name</label><input type="text" value={itemName} onChange={(e) => setItemName(e.target.value)} required /></div>
                        <div className="form-field"><label>Material</label><input type="text" value={material} onChange={(e) => setMaterial(e.target.value)} placeholder="e.g., Brass" /></div>
                        <div className="form-field" style={{gridColumn: 'span 2'}}><label>Item Description</label><textarea value={itemDescription} onChange={(e) => setItemDescription(e.target.value)} rows="3"></textarea></div>
                        <div className="form-field"><label>Linked Product ID (Optional)</label><input type="text" value={linkedProductId} onChange={(e) => setLinkedProductId(e.target.value)} placeholder="e.g., TTJ001" /></div>
                    </div>
                </div>

                <div className="form-section">
                    <h3>Purchase Details</h3>
                    <div className="form-grid">
                        <div className="form-field"><label>Purchase Date</label><DatePicker selected={purchaseDate} onChange={(date) => setPurchaseDate(date)} dateFormat="dd/MM/yyyy" className="date-picker-input" required /></div>
                        <div className="form-field"><label>Purchase Currency</label><select value={currency} onChange={(e) => setCurrency(e.target.value)}><option value="INR">INR</option><option value="CNY">CNY</option><option value="MYR">MYR</option><option value="USD">USD</option><option value="SGD">SGD</option></select></div>
                        <div className="form-field"><label>Live Exchange Rate to MYR</label><input type="text" value={exchangeRate ? `1 ${currency} = ${exchangeRate.toFixed(4)} MYR` : (currencyApiError || 'Fetching...')} readOnly /></div>
                        <div className="form-field"><label>Price Per Item ({currency}) {showAsMYR(parseFloat(pricePerItem))}</label><input type="number" step="0.01" value={pricePerItem} onChange={(e) => setPricePerItem(e.target.value)} required /></div>
                        <div className="form-field"><label>Total Items</label><input type="number" value={totalItems} onChange={(e) => setTotalItems(e.target.value)} required /></div>
                        <div className="form-field"><label>Total Item Price ({currency}) {showAsMYR(totalItemPrice)}</label><input type="text" value={totalItemPrice.toFixed(2)} readOnly /></div>
                        <div className="form-field"><label>Total Weight (in KG)</label><input type="number" step="0.1" value={totalWeight} onChange={(e) => setTotalWeight(e.target.value)} /></div>
                        <div className="form-field"><label>Weight Per Item (KG)</label><input type="text" value={weightPerItem.toFixed(1)} readOnly /></div>
                    </div>
                </div>

                <div className="form-section">
                    <h3>Vendor Shipping</h3>
                    <div className="form-grid">
                        <div className="form-field"><label>Vendor Shipping Method</label><select value={vendorShippingMethod} onChange={(e) => setVendorShippingMethod(e.target.value)}><option value="Agent">Agent</option><option value="Direct">Direct</option></select></div>
                        <div className="form-field"><label>Vendor Shipping Date</label><DatePicker selected={vendorShippingDate} onChange={(date) => setVendorShippingDate(date)} dateFormat="dd/MM/yyyy" className="date-picker-input" /></div>
                        <div className="form-field"><label>Vendor Shipping Cost ({currency}) {showAsMYR(parseFloat(vendorShippingCost))}</label><input type="number" step="0.01" value={vendorShippingCost} onChange={(e) => setVendorShippingCost(e.target.value)} /></div>
                        <div className="form-field"><label>Cost Per KG ({currency}) - Vendor {showAsMYR(costPerKgVendor)}</label><input type="text" value={costPerKgVendor.toFixed(2)} readOnly /></div>
                        <div className="form-field summary-box"><label>Total Purchase Cost ({currency})</label><h4>{totalPurchaseCost.toFixed(2)} {showAsMYR(totalPurchaseCost)}</h4><small>(Total Item Price + Vendor Shipping)</small></div>
                    </div>
                </div>

                <div className="form-section">
                    <h3>Vendor</h3>
                    <div className="form-grid"><div className="form-field"><label>Vendor Name</label><select value={selectedVendorId} onChange={(e) => setSelectedVendorId(e.target.value)} required><option value="">-- Select a Vendor --</option>{vendors.map(v => <option key={v.id} value={v.id}>{v.companyName}</option>)}</select></div><div className="form-field"><label>Vendor Address</label><input type="text" value={vendors.find(v=>v.id===selectedVendorId)?.address || ''} readOnly /></div><div className="form-field"><label>Vendor Contact</label><input type="text" value={vendors.find(v=>v.id===selectedVendorId)?.contactNumber || ''} readOnly /></div></div>
                </div>
                
                {vendorShippingMethod === 'Agent' && (<div className="form-section"><h3>Agent Shipping</h3><div className="form-grid"><div className="form-field"><label>Agent Company</label><select value={selectedAgentId} onChange={(e) => setSelectedAgentId(e.target.value)} required><option value="">-- Select an Agent --</option>{agents.map(a => <option key={a.id} value={a.id}>{a.companyName}</option>)}</select></div><div className="form-field"><label>Agent Address</label><input type="text" value={agents.find(a=>a.id===selectedAgentId)?.address || ''} readOnly /></div><div className="form-field"><label>Agent Contact</label><input type="text" value={agents.find(a=>a.id===selectedAgentId)?.contactNumber || ''} readOnly /></div><div className="form-field"><label>Agent Shipping Date</label><DatePicker selected={agentShippingDate} onChange={(date) => setAgentShippingDate(date)} dateFormat="dd/MM/yyyy" className="date-picker-input" /></div><div className="form-field"><label>Agent Shipping Cost ({currency}) {showAsMYR(parseFloat(agentShippingCost))}</label><input type="number" step="0.01" value={agentShippingCost} onChange={(e) => setAgentShippingCost(e.target.value)} /></div><div className="form-field"><label>Cost Per KG ({currency}) - Agent {showAsMYR(costPerKgAgent)}</label><input type="text" value={costPerKgAgent.toFixed(2)} readOnly /></div><div className="form-field"><label>Shipping Method</label><select><option value="Air Freight">Air Freight</option><option value="Sea Freight">Sea Freight</option></select></div><div className="form-field summary-box" style={{backgroundColor: 'var(--bg-accent-yellow)'}}><label>Total Import Cost ({currency})</label><h4>{totalImportCost.toFixed(2)} {showAsMYR(totalImportCost)}</h4><small>(Total Purchase Cost + Agent Shipping)</small></div></div></div>)}
                
                <div className="form-section profit-analysis-section"><h3>Profit Analysis (MYR)</h3><div className="form-grid"><div className="form-field"><label>Total Landing Cost (RM)</label><input type="text" value={totalLandingCostMYR.toFixed(2)} readOnly /></div><div className="form-field"><label>Per Unit Cost (RM)</label><input type="text" value={perUnitCostMYR.toFixed(2)} readOnly /></div><div className="form-field"><label>Selling Price (RM)</label><input type="number" step="0.01" placeholder="Enter selling price" value={sellingPriceMYR} onChange={(e) => setSellingPriceMYR(e.target.value)} style={{borderColor: 'var(--brand-success)'}}/></div><div className="form-field"><label>Received Date</label><DatePicker selected={receivedDate} onChange={(date) => setReceivedDate(date)} dateFormat="dd/MM/yyyy" className="date-picker-input" /></div><div className="form-field"><label>Profit (RM)</label><input type="text" value={profitMYR.toFixed(2)} readOnly style={{color: profitMYR < 0 ? 'var(--brand-danger)' : 'var(--brand-success)', fontWeight: 'bold'}} /></div><div className="form-field"><label>Profit Percentage (%)</label><input type="text" value={profitPercentage !== null ? profitPercentage.toFixed(2) + '%' : 'N/A'} readOnly /></div><div className="form-field"><label>Selling Status</label><SellingStatus percentage={profitPercentage} /></div></div></div>
            </div>
            <div className="product-form-sidebar">
                <div className="form-section">
                    <h3>Product Image</h3>
                    <div className="form-field">
                        <label>Upload Image</label>
                        <input type="file" onChange={handleImageChange} accept="image/*" />
                    </div>
                    {imagePreview && (
                        <div className="form-field">
                            <label>Image Preview</label>
                            <div className="image-preview-box">
                                <img src={imagePreview} alt="Preview" />
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div className="form-actions">
                <button type="submit" className="submit-button" disabled={isSubmitting}>{isSubmitting ? 'Saving...' : (productIdToEdit ? 'Update Batch' : 'Save Product Record')}</button>
                <button type="button" className="cancel-button" onClick={() => setView('dashboard')}>Cancel</button>
                {!productIdToEdit && (<button type="button" className="add-another-button" onClick={onAddNew}>+ Add Another</button>)}
                {submitMessage && <p className="popup-message">{submitMessage}</p>}
            </div>
        </form>
    );
};

export default ProductForm;